import { useState } from "react";
import type { Project, Repo } from "../lib/types";
import type { DiveConnection } from "../lib/dive";
import { initiateDiveOAuth } from "../lib/dive";
import { clearAuth } from "../lib/auth";
import { generateId } from "../lib/types";
import { toast } from "./Toast";

interface Props {
  login: string | null;
  projects: Project[];
  repos: Repo[];
  userId: string;
  diveConnection: DiveConnection | null;
  onUpdateProject: (project: Project) => void;
  onDeleteProject: (id: string) => void;
  onAddRepo: (repo: Repo) => void;
  onDeleteRepo: (slug: string) => void;
  onCreateProject: (project: Project) => void;
  onDisconnectDive: () => void;
}

function parseRepoSlug(input: string): string | null {
  input = input.trim();
  if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(input)) return input;
  const sshMatch = input.match(/^git@[^:]+:([^/]+\/[^/]+?)(?:\.git)?$/);
  if (sshMatch) return sshMatch[1];
  try {
    const url = new URL(input);
    const parts = url.pathname.replace(/^\//, "").replace(/\.git$/, "").split("/");
    if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
  } catch { /* not a URL */ }
  return null;
}

const SERVER_BASE = import.meta.env.VITE_API_BASE ?? window.location.origin;
const MCP_URL = `${SERVER_BASE}/mcp`;

export function Settings({
  login,
  projects,
  repos,
  userId,
  diveConnection,
  onUpdateProject,
  onDeleteProject,
  onAddRepo,
  onDeleteRepo,
  onCreateProject,
  onDisconnectDive,
}: Props) {
  const [repoInput, setRepoInput] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [newProjectName, setNewProjectName] = useState("");

  const handleAddRepo = (e: React.FormEvent) => {
    e.preventDefault();
    const slug = parseRepoSlug(repoInput);
    if (!slug) {
      toast({ kind: "error", message: "Invalid repo format. Use owner/name, SSH, or HTTPS URL." });
      return;
    }
    if (repos.some((r) => r.slug === slug)) {
      toast({ kind: "error", message: `Repo ${slug} already added.` });
      return;
    }
    const now = Date.now();
    onAddRepo({
      slug,
      userId,
      url: repoUrl.trim() || repoInput.trim(),
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    });
    setRepoInput("");
    setRepoUrl("");
    toast({ kind: "success", message: `Added ${slug}` });
  };

  const handleCreateProject = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    const now = Date.now();
    onCreateProject({
      id: generateId(),
      userId,
      name: newProjectName.trim(),
      repos: [],
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    });
    setNewProjectName("");
  };

  const toggleRepoInProject = (project: Project, slug: string) => {
    const repoSet = new Set(project.repos);
    if (repoSet.has(slug)) repoSet.delete(slug);
    else repoSet.add(slug);
    onUpdateProject({ ...project, repos: Array.from(repoSet), updatedAt: Date.now() });
  };

  return (
    <div className="flex flex-col gap-6 px-4 py-4 overflow-y-auto pb-8">
      {/* GitHub account */}
      <section>
        <h3 className="font-syne text-base font-bold mb-3">GitHub Account</h3>
        {login ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3">
            <div className="flex items-center gap-3">
              <img
                src={`https://github.com/${login}.png?size=32`}
                alt={login}
                className="w-8 h-8 rounded-full"
              />
              <span className="text-sm font-mono text-gray-800">@{login}</span>
            </div>
            <button
              onClick={() => { clearAuth(); window.location.reload(); }}
              className="text-xs font-mono text-red-500 hover:text-red-700 transition-colors"
            >
              Sign out
            </button>
          </div>
        ) : (
          <button
            onClick={() => window.location.href = "/authorize"}
            className="w-full bg-gray-900 text-white rounded-xl py-3 text-sm font-mono font-bold"
          >
            Sign in with GitHub
          </button>
        )}
      </section>

      {/* Repositories */}
      <section>
        <h3 className="font-syne text-base font-bold mb-3">Repositories</h3>
        <form onSubmit={handleAddRepo} className="flex flex-col gap-2 mb-3">
          <input
            type="text"
            value={repoInput}
            onChange={(e) => setRepoInput(e.target.value)}
            placeholder="owner/repo, SSH or HTTPS URL"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
          />
          <button
            type="submit"
            disabled={!repoInput.trim()}
            className="bg-gray-900 text-white rounded-lg py-2.5 text-sm font-mono font-bold disabled:opacity-40 hover:bg-gray-700 transition-colors"
          >
            Add Repo
          </button>
        </form>
        {repos.length > 0 && (
          <div className="flex flex-col gap-2">
            {repos.map((r) => (
              <div
                key={r.slug}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2.5"
              >
                <span className="text-sm font-mono text-gray-700">{r.slug}</span>
                <button
                  onClick={() => onDeleteRepo(r.slug)}
                  className="text-gray-300 hover:text-red-500 text-xs transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Projects */}
      <section>
        <h3 className="font-syne text-base font-bold mb-3">Projects</h3>
        <form onSubmit={handleCreateProject} className="flex gap-2 mb-3">
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="Project name"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
          />
          <button
            type="submit"
            disabled={!newProjectName.trim()}
            className="bg-gray-900 text-white rounded-lg px-4 text-sm font-mono font-bold disabled:opacity-40"
          >
            Create
          </button>
        </form>
        {projects.map((project) => (
          <div key={project.id} className="rounded-xl border border-gray-100 bg-white p-3 mb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-mono font-bold text-gray-800">{project.name}</span>
              <button
                onClick={() => onDeleteProject(project.id)}
                className="text-xs font-mono text-red-400 hover:text-red-600"
              >
                Delete
              </button>
            </div>
            {repos.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-xs text-gray-400 font-mono mb-1">Repos in this project:</p>
                {repos.map((r) => (
                  <label key={r.slug} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={project.repos.includes(r.slug)}
                      onChange={() => toggleRepoInProject(project, r.slug)}
                      className="rounded"
                    />
                    <span className="text-xs font-mono text-gray-600">{r.slug}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Dive integration */}
      <section>
        <h3 className="font-syne text-base font-bold mb-3">Dive</h3>
        {diveConnection ? (
          <div className="rounded-xl border border-gray-100 bg-white p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                <span className="text-sm font-mono text-gray-700">Connected</span>
              </div>
              <button
                onClick={onDisconnectDive}
                className="text-xs font-mono text-red-400 hover:text-red-600 transition-colors"
              >
                Disconnect
              </button>
            </div>
            <p className="text-xs text-gray-400 font-mono mt-1">{diveConnection.diveUrl}</p>
          </div>
        ) : (
          <DiveConnectSection />
        )}
      </section>

      {/* MCP server URL */}
      <section>
        <h3 className="font-syne text-base font-bold mb-2">MCP Server</h3>
        <p className="text-xs text-gray-500 font-mono mb-2 leading-relaxed">
          Add this URL to Claude Code or claude.ai to let Claude refine your ideas.
        </p>
        <div className="flex gap-2 items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
          <code className="flex-1 text-xs font-mono text-gray-700 break-all">{MCP_URL}</code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(MCP_URL).catch(console.error);
              toast({ kind: "success", message: "Copied!" });
            }}
            className="text-xs font-mono text-gray-500 hover:text-gray-900 shrink-0"
          >
            Copy
          </button>
        </div>
        <div className="mt-3 text-xs text-gray-400 font-mono leading-relaxed space-y-1">
          <p>
            <strong>Claude Code:</strong>{" "}
            <code>claude mcp add --transport http crux {MCP_URL}</code>
          </p>
          <p>
            <strong>claude.ai:</strong> Settings → Connectors → add the URL above.
          </p>
        </div>
      </section>
    </div>
  );
}

const DIVE_URL = import.meta.env.VITE_DIVE_URL as string | undefined;

function DiveConnectSection() {
  const [customUrl, setCustomUrl] = useState(DIVE_URL ?? "");
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    const url = customUrl.trim();
    if (!url) return;
    setConnecting(true);
    try {
      await initiateDiveOAuth(url);
      // Browser redirects — this line won't be reached
    } catch (err) {
      toast({ kind: "error", message: err instanceof Error ? err.message : "Failed to start OAuth" });
      setConnecting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {!DIVE_URL && (
        <input
          type="url"
          value={customUrl}
          onChange={(e) => setCustomUrl(e.target.value)}
          placeholder="https://your-dive-url.pages.dev"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
        />
      )}
      <button
        onClick={handleConnect}
        disabled={!customUrl.trim() || connecting}
        className="bg-gray-900 text-white rounded-xl py-2.5 text-sm font-mono font-bold disabled:opacity-40 hover:bg-gray-700 transition-colors"
      >
        {connecting ? "Redirecting…" : "Connect to Dive"}
      </button>
      <p className="text-xs text-gray-400 font-mono leading-relaxed">
        You'll be asked to approve the connection in Dive.
      </p>
    </div>
  );
}
